import React from 'react';
import { MagnifyingGlassIcon, PlusIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';

export type TenantStatusFilter = 'all' | 'active' | 'inactive';
export type TenantSortOption = 'name' | 'users_desc' | 'created_desc';

interface PlatformTenantToolbarProps {
  search: string;
  onSearchChange: (v: string) => void;
  statusFilter: TenantStatusFilter;
  onStatusFilterChange: (v: TenantStatusFilter) => void;
  sortBy: TenantSortOption;
  onSortChange: (v: TenantSortOption) => void;
  onNewTenant: () => void;
}

const selectClass =
  'h-10 rounded-lg border border-slate-200 bg-white pl-3 pr-8 text-sm font-medium text-slate-700 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200';

export const PlatformTenantToolbar: React.FC<PlatformTenantToolbarProps> = ({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  sortBy,
  onSortChange,
  onNewTenant,
}) => (
  <div className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm ring-1 ring-slate-900/5">
    <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
      <div className="flex min-w-0 flex-1 flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative min-w-0 flex-1 lg:max-w-xl">
          <MagnifyingGlassIcon
            className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
            aria-hidden
          />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Buscar por nombre, slug o email de contacto…"
            className={clsx(
              'h-11 w-full rounded-xl border border-slate-200 bg-slate-50/50 pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400',
              'transition-colors focus:border-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-200'
            )}
            autoComplete="off"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 text-slate-500">
            <FunnelIcon className="h-4 w-4 shrink-0" aria-hidden />
            <span className="sr-only">Filtros</span>
          </div>
          <select
            className={selectClass}
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value as TenantStatusFilter)}
            aria-label="Filtrar por estado de funeraria"
          >
            <option value="all">Todas las funerarias</option>
            <option value="active">Solo activas</option>
            <option value="inactive">Solo suspendidas</option>
          </select>
          <select
            className={selectClass}
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value as TenantSortOption)}
            aria-label="Ordenar listado"
          >
            <option value="name">Orden: nombre A–Z</option>
            <option value="users_desc">Orden: más usuarios</option>
            <option value="created_desc">Orden: más recientes</option>
          </select>
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-end border-t border-slate-100 pt-4 xl:border-t-0 xl:pt-0">
        <button
          type="button"
          onClick={onNewTenant}
          className={clsx(
            'inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl px-5 text-sm font-semibold text-white shadow-md transition-all',
            'bg-gradient-to-r from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700 hover:shadow-lg',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2',
            'xl:w-auto'
          )}
        >
          <PlusIcon className="h-5 w-5" aria-hidden />
          Nueva funeraria
        </button>
      </div>
    </div>
    <p className="mt-3 text-xs text-slate-400">
      Los filtros y orden aplican sobre el listado actual. La búsqueda consulta el servidor.
    </p>
  </div>
);
